import React from 'react';
import { Form, Button, Alert, Accordion, Row, Col, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { ArrowRight } from 'react-bootstrap-icons';

function getAutomationHeader(automation, sensors, actionPins) {
  const { trigger, action } = automation;
  const actionPinObj = actionPins.find((ap) => ap.pin === action.pin);
  const actionPinName = actionPinObj ? actionPinObj.name || actionPinObj.type : action.pin;

  if (trigger.type === 'sensor') {
    const sensor = sensors.find((s) => s.sensorID === trigger.sensor);
    const sensorName = sensor ? sensor.name || sensor.sensorType : trigger.sensor;
    if (sensorName && trigger.operator && trigger.threshold && action.type && action.pin) {
      return `${sensorName} ${trigger.operator} ${trigger.threshold} → ${action.type} ${actionPinName}`;
    }
  } else if (trigger.type === 'time') {
    if (trigger.startTime && trigger.endTime && action.type && action.pin) {
      return `${trigger.startTime} - ${trigger.endTime} → ${action.type} ${actionPinName}`;
    }
  }
  return 'New Automation Pair';
}

const DeviceAutomationsPanel = ({ sensors, actionPins, automations, setAutomations }) => {
  const addAutomation = () => {
    const newId = automations.length ? Math.max(...automations.map((a) => a.id)) + 1 : 0;
    setAutomations([...automations, {
      id: newId,
      trigger: { type: 'sensor', sensor: '', operator: '<', threshold: '', startTime: '', endTime: '' },
      action: { type: 'Activate', pin: '' },
    }]);
  };

  const updateAutomation = (id, section, field, value) => {
    setAutomations(automations.map((a) => {
      if (a.id === id) {
        return { ...a, [section]: { ...a[section], [field]: value } };
      }
      return a;
    }));
  };

  const removeAutomation = (id) => {
    setAutomations(automations.filter((a) => a.id !== id));
  };

  return (
    <div className="setup-panel">
      <h4 className="mb-3">Manage Automations</h4>
      {sensors.length === 0 && (
        <Alert variant="info">Add some sensors first to create automations!</Alert>
      )}
      <Accordion>
        {automations.map((automation) => (
          <Accordion.Item eventKey={automation.id.toString()} key={automation.id}>
            <Accordion.Header>{getAutomationHeader(automation, sensors, actionPins)}</Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Trigger Type</Form.Label>
                  <Form.Select
                    value={automation.trigger.type || 'sensor'}
                    onChange={(e) => updateAutomation(automation.id, 'trigger', 'type', e.target.value)}
                  >
                    <option value="sensor">Sensor-Based</option>
                    <option value="time">Time-Based</option>
                  </Form.Select>
                </Col>
              </Row>

              <Row className="align-items-end">
                {automation.trigger.type === 'sensor' ? (
                  <>
                    <Col md={3} className="pe-0">
                      <div className="d-flex align-items-center mb-2">
                        <h6 className="mb-0 text-muted me-2">Sensor:</h6>
                      </div>
                      <Form.Select
                        value={automation.trigger.sensor}
                        onChange={(e) => updateAutomation(automation.id, 'trigger', 'sensor', e.target.value)}
                        disabled={sensors.length === 0}
                      >
                        <option value="">Select a sensor</option>
                        {sensors.map((sensor, idx) => (
                          <option key={idx} value={sensor.sensorID}>
                            {sensor.name || sensor.sensorType}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={1} className="px-1">
                      <ButtonGroup className="w-100">
                        <ToggleButton
                          id={`less-than-${automation.id}`}
                          type="radio"
                          variant="outline-secondary"
                          name={`operator-${automation.id}`}
                          checked={automation.trigger.operator === '<'}
                          value="<"
                          onChange={(e) => updateAutomation(automation.id, 'trigger', 'operator', e.currentTarget.value)}
                        >
                          {'<'}
                        </ToggleButton>
                        <ToggleButton
                          id={`greater-than-${automation.id}`}
                          type="radio"
                          variant="outline-secondary"
                          name={`operator-${automation.id}`}
                          checked={automation.trigger.operator === '>'}
                          value=">"
                          onChange={(e) => updateAutomation(automation.id, 'trigger', 'operator', e.currentTarget.value)}
                        >
                          {'>'}
                        </ToggleButton>
                      </ButtonGroup>
                    </Col>
                    <Col md={2} className="ps-0">
                      <div className="d-flex align-items-center mb-2">
                        <h6 className="mb-0 text-muted me-2">Threshold:</h6>
                      </div>
                      <Form.Control
                        type="number"
                        value={automation.trigger.threshold || ''}
                        onChange={(e) => updateAutomation(automation.id, 'trigger', 'threshold', e.target.value)}
                        placeholder="e.g., 30"
                      />
                    </Col>
                  </>
                ) : (
                  <Col md={6} className="pe-0">
                    <div className="d-flex align-items-center mb-2">
                      <h6 className="mb-0 text-muted me-2">Active Window:</h6>
                      <small className="text-muted">(Set start {'>'} end for overnight)</small>
                    </div>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="time"
                        value={automation.trigger.startTime || ''}
                        onChange={(e) => updateAutomation(automation.id, 'trigger', 'startTime', e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <span className="align-self-center">to</span>
                      <Form.Control
                        type="time"
                        value={automation.trigger.endTime || ''}
                        onChange={(e) => updateAutomation(automation.id, 'trigger', 'endTime', e.target.value)}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Col>
                )}

                <Col md={1} className="text-center px-0">
                  <div className="d-flex align-items-end h-100">
                    <ArrowRight size={24} className="text-muted" />
                  </div>
                </Col>

                <Col md={2}>
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="mb-0 text-muted me-2">Action:</h6>
                  </div>
                  <Form.Select
                    value={automation.action.type}
                    onChange={(e) => updateAutomation(automation.id, 'action', 'type', e.target.value)}
                  >
                    <option value="Activate">Activate</option>
                    <option value="Deactivate">Deactivate</option>
                  </Form.Select>
                </Col>
                <Col md={2} className="ps-0">
                  <div className="d-flex align-items-center mb-2">
                    <h6 className="mb-0 text-muted me-2">Pin:</h6>
                  </div>
                  <Form.Select
                    value={automation.action.pin}
                    onChange={(e) => updateAutomation(automation.id, 'action', 'pin', e.target.value)}
                    disabled={actionPins.length === 0}
                  >
                    <option value="">Select an action pin</option>
                    {actionPins.map((ap, idx) => (
                      <option key={idx} value={ap.pin}>
                        {ap.name || ap.type} ({ap.pin})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Button
                variant="outline-danger"
                size="sm"
                className="mt-3"
                onClick={() => removeAutomation(automation.id)}
              >
                Delete Pair
              </Button>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
      <Button
        variant="outline-primary"
        className="w-100 mt-2"
        onClick={addAutomation}
        disabled={sensors.length === 0}
      >
        + Add Automation Pair
      </Button>
    </div>
  );
};

export default DeviceAutomationsPanel;